import type { BoundingBox } from '@vladmandic/face-api';

import type { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import type { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { isLowLightForDocument } from 'verify/lib/utils/document';
import type { LuminosityData } from 'verify/lib/utils/image';

export enum BarcodeTriggerType {
  FrontSide,
  Capture,
  HoldStill,
  DetectedLowLight,
  None,
}

export interface BarcodeTriggerParams {
  successfullyScanned: boolean;
  detectedGuards: number;
  faceBoundingBox: BoundingBox;
  requiresSide: GovernmentIdSide;
  luminosityData: LuminosityData | null;
}

/**
 * NOTE: DO NOT CHANGE ANY TIMINGS BELOW.
 *
 * We've committed to not changing timings for Chime.
 * If changing, please ask in #c-chime-internal.
 */
export const DETECT_INTERVAL = 300;
export const SIDE_THRESHOLD = 0.3;
export const BOUNDING_BOX_WIDTH = 0.27;
export const BOUNDING_BOX_HEIGHT = 0.32;

export const BARCODE_TRIGGER_CONFIGS: Array<
  FrameTriggerConfig<
    BarcodeTriggerType,
    BarcodeTriggerParams,
    StepGovernmentIdConfig['localizations']['capture-page']
  >
> = [
  {
    type: BarcodeTriggerType.FrontSide,
    shouldShow: matchesIdentityCard,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('barcode-scanner.hint.back-side'),
  },
  {
    type: BarcodeTriggerType.Capture,
    shouldShow: (params) => {
      if (params.requiresSide === GovernmentIdSide.BarcodePdf417) {
        return params.detectedGuards >= 2;
      } else {
        return params.detectedGuards >= 1;
      }
    },
    showAfterFrames: 3,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('barcode-scanner.hint.hold-still'),
  },
  {
    type: BarcodeTriggerType.HoldStill,
    shouldShow: (params) => params.detectedGuards >= 1,
    showAfterFrames: 1,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('barcode-scanner.hint.hold-still'),
  },
  {
    type: BarcodeTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: BarcodeTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export function detectsLowLight(params: BarcodeTriggerParams): boolean {
  return isLowLightForDocument(params.luminosityData);
}

export function matchesIdentityCard(params: {
  faceBoundingBox: { width: number; height: number; left: number };
}) {
  if (params.faceBoundingBox == null) {
    return false;
  }

  const centerX = params.faceBoundingBox.left + params.faceBoundingBox.width / 2;

  return (
    (centerX < SIDE_THRESHOLD || centerX > 1 - SIDE_THRESHOLD) &&
    params.faceBoundingBox.width < BOUNDING_BOX_WIDTH + 0.05 &&
    params.faceBoundingBox.height < BOUNDING_BOX_HEIGHT + 0.05
  );
}
